import React, { useState } from 'react';
import Tooltip from '../Tooltip';
import Icon from '../Icon';
import { TIME_STEP, round, formatTime, parseTime } from './momentTime';
import './TimeRange.css';

/**
 * TimeRange — a start → end time window, compact: the two times side by
 * side with an arrow between them, and each time's −/+/"use current time"
 * buttons underneath it. Used by the Add panel and the edit forms.
 *
 * Each time can also be typed, to the tenth of a second ("0:03.7", or "3.7"
 * seconds): it is taken on Enter or on leaving the field; anything that
 * isn't a time puts the old one back.
 *
 * The end is optional (optionalEnd): without one (end === null), a subtitle
 * or annotation lasts until the next subtitle starts (src/utils/timeline.js).
 * The link button "syncs" a set end with the next subtitle by clearing it,
 * once there is a next subtitle to end at.
 *
 * Props:
 *   start, end — seconds; end may be null with optionalEnd
 *   onChange({ start, end }) — the new window (a set end is kept ≥ start)
 *   currentTime — playback time, for the "use current time" buttons
 *   keepLength — optional: setting the start to the current time also moves
 *                the end to start + keepLength
 *   optionalEnd — the end may be left open
 *   nextStart — when the next subtitle starts (null if none), for optionalEnd
 *   testIdPrefix — optional: data-testid prefix for the two time displays
 */
function TimeRange({ start, end, onChange, currentTime, keepLength, optionalEnd, nextStart = null, testIdPrefix }) {
  const [note, setNote] = useState('');
  const open = end === null || end === undefined;

  const change = (w) => { setNote(''); onChange(w); };
  const setStart = (s) => {
    const next = Math.max(0, round(s));
    change({ start: next, end: open ? null : Math.max(end, next) });
  };
  const setEnd = (e) => change({ start, end: Math.max(start, round(e)) });
  const startNow = () => {
    const t = Math.max(0, round(currentTime));
    if (keepLength != null) change({ start: t, end: round(t + keepLength) });
    else change({ start: t, end: open ? null : Math.max(end, t) });
  };
  const syncWithNext = () => {
    if (nextStart === null) {
      setNote('There is no next subtitle yet, so the end time stays.');
      return;
    }
    change({ start, end: null });
  };

  const field = (which, value, set) => (
    <TimeField value={value} onCommit={set} label={`${which[0].toUpperCase()}${which.slice(1)} time`}
      testId={testIdPrefix && `${testIdPrefix}-${which}`} />
  );

  const buttons = (which, { dec, inc, now, decDisabled, incLabel }) => (
    <div className="time-range-buttons">
      {dec && (
        <button type="button" className="time-range-btn" onClick={dec} disabled={decDisabled}
          aria-label={`Decrease ${which} time`}>−</button>
      )}
      <button type="button" className="time-range-btn" onClick={inc}
        aria-label={incLabel || `Increase ${which} time`}>+</button>
      <Tooltip label="Use current video time">
        <button type="button" className="time-range-btn" onClick={now}
          aria-label={`Set ${which} to current time`}><Icon name="timer" size={15} /></button>
      </Tooltip>
    </div>
  );

  const nextLabel = nextStart === null ? 'until the next subtitle' : `until the next (${formatTime(nextStart)})`;

  return (
    <div className="time-range-wrap">
      <div className="time-range">
        <div className="time-range-point">
          {field('start', start, setStart)}
          {buttons('start', {
            dec: () => setStart(start - TIME_STEP),
            inc: () => setStart(start + TIME_STEP),
            now: startNow,
            decDisabled: start <= 0,
          })}
        </div>
        <span className="time-range-arrow" aria-label="to"><Icon name="arrow-right" size={18} /></span>
        <div className="time-range-point">
          {open ? (
            <span className="time-range-value time-range-value--open" data-testid={testIdPrefix && `${testIdPrefix}-end`}>
              {nextLabel}
            </span>
          ) : (
            field('end', end, setEnd)
          )}
          <div className="time-range-end-buttons">
            {open
              ? buttons('end', {
                inc: () => setEnd(start + 1),
                now: () => setEnd(currentTime),
                incLabel: 'Add an end time',
              })
              : buttons('end', {
                dec: () => setEnd(end - TIME_STEP),
                inc: () => setEnd(end + TIME_STEP),
                now: () => setEnd(currentTime),
                decDisabled: end <= start,
              })}
            {optionalEnd && !open && (
              <Tooltip label="Sync with the next subtitle: clear this end time, so it ends when the next one starts">
                <button type="button" className="time-range-btn time-range-sync" onClick={syncWithNext}
                  aria-label="Sync end with the next subtitle"><Icon name="link" size={15} /></button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
      {note && <p className="time-range-note" role="status">{note}</p>}
    </div>
  );
}

/** A time the teacher can type: shows the time, takes a typed one on Enter or blur. */
function TimeField({ value, onCommit, label, testId }) {
  // What is being typed, else null: the time shows (and follows the
  // buttons or the video) whenever the field isn't being typed in.
  const [draft, setDraft] = useState(null);

  const commit = () => {
    if (draft === null) return;
    const t = parseTime(draft);
    setDraft(null);
    if (t !== null && t !== value) onCommit(t);
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      className="time-range-value time-range-input"
      value={draft === null ? formatTime(value) : draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={(e) => e.target.select()}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        if (e.key === 'Escape') setDraft(null);
      }}
      aria-label={label}
      data-testid={testId}
      size={6}
    />
  );
}

export default TimeRange;
